import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Divider,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import BaseInstance from '../../../../Services/AxiosInstance/BaseInstance';
import { KNOWLEDGE_BASE } from '../../../../Services/Endpoint';
import { KnowledgeBase } from '../../../../Shared-ui/Types/KnowledgeBase';

interface KnowledgeBaseConfigPanelProps {
  nodeId: string;
  nodeData: {
    label: string;
    knowledgeBaseId?: string;
    knowledgeBaseName?: string;
  };
  onSave: (nodeId: string, data: any) => void;
  onClose: () => void;
}

const KnowledgeBaseConfigPanel: React.FC<KnowledgeBaseConfigPanelProps> = ({
  nodeId,
  nodeData,
  onSave,
  onClose,
}) => {
  const [name, setName] = useState(nodeData.label || '');
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState(nodeData.knowledgeBaseId || '');
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchKnowledgeBases = async () => {
      try {
        setLoading(true);
        const response = await BaseInstance.get(`${KNOWLEDGE_BASE}?page=1&page_size=100`);
        console.log('Knowledge Base API response:', response.data); // Debug log
        // Use the same pattern as existing implementations
        const kbData = response.data?.items || [];
        setKnowledgeBases(kbData);
      } catch (error) {
        console.error('Failed to fetch knowledge bases:', error);
        setKnowledgeBases([]);
      } finally {
        setLoading(false);
      }
    };

    fetchKnowledgeBases();
  }, []);

  // Sync when a different Knowledge Base node is selected while panel is open
  useEffect(() => {
    setName(nodeData.label || '');
    setSelectedKnowledgeBase(nodeData.knowledgeBaseId || '');
  }, [nodeId, nodeData]);

  const handleSave = () => {
    const selectedKB = knowledgeBases.find(kb => kb.id === selectedKnowledgeBase);
    const updatedData = {
      label: name,
      knowledgeBaseId: selectedKnowledgeBase,
      knowledgeBaseName: selectedKB?.name || '',
    };
    onSave(nodeId, updatedData);
  };

  return (
    <Paper
      sx={{
        width: 400,
        height: '100vh',
        p: 3,
        borderRadius: 0,
        borderLeft: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        right: 0,
        zIndex: 1200,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Configure Knowledge Base
        </Typography>
        <Button
          onClick={onClose}
          sx={{ minWidth: 'auto', p: 1 }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, overflow: 'auto', pt: 1 }}>
        <TextField
          label="Node Name"
          value={name}
          onChange={(e) => {
            if(e.target.value.length <= 40){
              setName(e.target.value)
            }
          }}
          fullWidth
          variant="outlined"
          size="small"
        />

        <FormControl fullWidth size="small">
          <InputLabel>Knowledge Base</InputLabel>
          <Select
            value={selectedKnowledgeBase}
            onChange={(e) => setSelectedKnowledgeBase(e.target.value)}
            label="Knowledge Base"
            disabled={loading}
          >
            {loading ? (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Loading knowledge bases...
              </MenuItem>
            ) : (
              Array.isArray(knowledgeBases) ? knowledgeBases.map((kb) => (
                <MenuItem key={kb.id} value={kb.id}>
                  <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {kb.name}
                    </Typography>
                    {kb.description && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#666',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '330px',
                        }}
                      >
                        {kb.description}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              )) : (
                <MenuItem disabled>
                  No knowledge bases available
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          fullWidth
          disabled={!name || !selectedKnowledgeBase}
        >
          Save Configuration
        </Button>
      </Box>
    </Paper>
  );
};

export default KnowledgeBaseConfigPanel;
